import React, { useEffect, useState } from "react";
import { BiFilter, BiPlus } from "react-icons/bi";
import { LuSearch } from "react-icons/lu";
import { TbFilterX } from "react-icons/tb";
import { getNewHeader } from "../../../utils/helpers";
import useOutsideClick from "../../hooks/useOutsideClick";
import ConditionGroupItem from "./ConditionGroupItem";
import FilterItem from "./FilterItem";
import "./filter.css";

/**
 * FilterSection Component
 * 
 * Main filter UI container managing complex nested filter conditions with AND/OR logic.
 * Supports adding individual conditions and groups of conditions. Transforms user-selected
 * filters into backend query format and submits via parent callback. Displays all filter
 * UI controls and manages filter state with useReducer patterns.
 * 
 * @param {Object} props - Component props
 * @param {Array} props.fields - Available fields for filtering
 * @param {Function} props.dispatch - Redux-style dispatch for filter state
 * @param {Function} props.submitSearch - Callback to submit filter query
 * @param {Function} props.resetFilter - Reset all filters callback
 * @param {string} props.argument - Current filter argument string
 * @param {string} props.collection - Data collection being filtered
 * @param {Array} props.filterConditionsArray - Current filter conditions state
 * @param {boolean} props.isLoading - Loading state indicator
 * @returns {JSX.Element} Filter management interface with condition builders
 */
const FilterSection = ({ props }) => {
  const {
    fields,
    dispatch,
    submitSearch,
    resetFilter,
    argument,
    collection,
    filterConditionsArray,
    isLoading,
  } = props;

  const [connective, setConnective] = useState("and");

  const { ref, isOpen, toggleNav } = useOutsideClick();

  // the condition object
  const conditionObj = {
    connective: "and",
    field: fields[0].actualName,
    operator: "contains",
    value: "",
    dtype: "text",
  };

  // the condition object
  const conditionGroupObj = {
    type: "group",
    connective: "",
    conditions: [],
  };

  const push = (obj) => {
    dispatch({
      stateArray: "filterConditionsArray",
      type: "push_array",
      value: obj,
    });
  };

  const update = (id, array) => {
    dispatch({
      stateArray: "filterConditionsArray",
      type: "update_array",
      index: id,
      newElement: { ...array },
    });
  };

  const remove = (id) => {
    dispatch({
      stateArray: "filterConditionsArray",
      type: "remove_item_from_array",
      index: id,
    });
  };

  const clear = () => {
    dispatch({ stateArray: "filterConditionsArray", type: "clear_array" });
  };

  const handleAddCondition = () => {
    // add condition object to the overall array
    push(conditionObj);
  };

  const handleAddConditionGroup = () => {
    // add condition group object to the overall array
    push(conditionGroupObj);
  };

  const handleSubmitFilter = (e) => {
    submitSearch(e);
  };

  const handleResetFilter = (e) => {
    clear(); // clear the array
    resetFilter(e);
  };

  const transformValueToBool = (value) => {
    if (value === "") return "";

    if (value.toLowerCase() === "active") {
      return true;
    } else {
      return false;
    }
  };

  // function to transform conditionObj array to the desired format
  const transform = (list) => {
    let connective;
    const conditions = [];

    const size = list.length;

    const dTypes = new Map();

    dTypes.set("text", "string");
    dTypes.set("number", "number");
    dTypes.set("date", "date");
    dTypes.set("boolean", "boolean");

    // Ensure the list contains one or more items and then specify the value of the connective
    // if (size === 0) return null

    if (size > 1) {
      connective = list[1]["connective"];
    } else connective = "and"; // Value of size is 1

    // Specify the conditions
    list.map((x) =>
      conditions.push({
        where: {
          field: getNewHeader(collection, x.field),
          dtype: dTypes.get(x.dtype),
        },
        operator: x.operator,
        value:
          x.dtype === "number"
            ? parseInt(x.value)
            : x.dtype === "boolean"
              ? transformValueToBool(x.value)
              : x.value,
      })
    );

    return {
      connective: connective,
      conditions: conditions,
      applied: argument["filter"]["applied"],
    };
  };

  // useEffect to set the argument to be sent to the backend
  useEffect(() => {
    dispatch({
      type: "update_state",
      state: "argument",
      value: { ...argument, filter: transform(filterConditionsArray) },
    });
  }, [filterConditionsArray]);

  return (
    <div ref={ref} className="dropdown shutdownfilterdiv">
      <span
        className={
          isLoading ? "disabled shutdownfilterbuttons" : "shutdownfilterbuttons"
        }
        filterapplied={argument["filter"]["applied"] ? "true" : "false"}
        data-tip="Filter results"
        onClick={!isLoading ? () => toggleNav() : () => { }}
      >
        <span>
          <BiFilter size={"1.5rem"} />
        </span>
        <span className="desktop">
          Filter
          <span
            style={{
              display: argument["filter"]["applied"] ? "inline" : "none",
            }}
          >
            ed
          </span>
        </span>
      </span>

      <div style={{ display: isOpen ? "block" : "none" }}>
        <div
          className="menu"
          onClick={(e) => {
            // do not close modal if anything inside modal content is clicked
            e.stopPropagation();
          }}
        >
          {filterConditionsArray === undefined ||
            filterConditionsArray.length === 0 ? (
            <div
              className="menu-header"
              style={{
                color: "rgba(77, 77, 77, 0.3)",
                paddingBottom: "16px",
              }}
            >
              No filter conditions are applied
            </div>
          ) : (
            <>
              <div
                className="menu-header"
                style={{ color: "rgb(102, 102, 102)" }}
              >
                In this view, show records
              </div>
              <div className="menu-content">
                {filterConditionsArray.map((item, index) => {
                  if (item.type?.includes("group")) {
                    return (
                      <ConditionGroupItem
                        key={index}
                        connective={connective}
                        setConnective={setConnective}
                        array={filterConditionsArray}
                        id={index}
                        remove={remove}
                        update={update}
                      />
                    );
                  }

                  return (
                    <FilterItem
                      key={index}
                      fields={fields}
                      connective={connective}
                      setConnective={setConnective}
                      array={filterConditionsArray}
                      id={index}
                      remove={remove}
                      update={update}
                    />
                  );
                })}
              </div>
            </>
          )}

          <div
            className="flex menu-footer"
            style={{ color: "rgb(102, 102, 102)" }}
          >
            <div
              className="flex gap-3 pointer"
              style={{ whiteSpace: "nowrap" }}
              onClick={handleAddCondition}
            >
              <BiPlus />
              <span>Add condition</span>
            </div>

            {filterConditionsArray.length > 0 && (
              <div
                className="flex pointer"
                style={{ whiteSpace: "nowrap", gap: "20px" }}
              >
                <TbFilterX
                  data-tip="Clear all filters"
                  onClick={handleResetFilter}
                  className=""
                  size={"1.2rem"}
                />

                <LuSearch
                  data-tip="Search"
                  className=""
                  onClick={handleSubmitFilter}
                  size={"1.2rem"}
                />
              </div>
            )}

            {/* Add condition group button */}
            {/* <div
              className='flex gap-3 pointer'
              style={{ whiteSpace: 'nowrap' }}
              onClick={handleAddConditionGroup}
            >
              <BiPlus />
              <span>Add condition group</span>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterSection;
